import { blue } from '@mui/material/colors';
import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import darcula from 'react-syntax-highlighter/dist/esm/styles/prism/darcula';
import oneLight from 'react-syntax-highlighter/dist/esm/styles/prism/one-light';

// The app's blue canvas gradient, used by both the main content area and the
// <body> (so any sliver behind the animating sidebar shows the same thing).
// Both are painted backgroundAttachment:fixed, so the two gradients coincide.
export function appBackground(mode) {
    return mode === 'dark'
        ? 'radial-gradient(1100px 480px at 50% -10%, rgba(96,165,250,0.16), transparent 60%), ' +
              'linear-gradient(180deg, #111c33 0%, #0b1120 70%)'
        : 'radial-gradient(1100px 480px at 50% -10%, rgba(59,130,246,0.10), transparent 60%), ' +
              'linear-gradient(180deg, #eef3fc 0%, #e7eef9 70%)';
}

// ────────────────────────────────────────────────────────────
// Shared component overrides applied to both themes
// ────────────────────────────────────────────────────────────
const sharedComponents = (mode) => ({
    MuiTooltip: {
        styleOverrides: {
            tooltip: {
                maxWidth: 'none',
                width: 'auto',
                fontSize: '0.75rem',
                borderRadius: 8,
                padding: '6px 12px',
            },
            arrow: {
                ...(mode === 'dark' ? { color: '#243352' } : { color: '#334155' }),
            },
        },
        defaultProps: {
            arrow: true,
            enterDelay: 400,
        },
    },
    MuiCssBaseline: {
        styleOverrides: {
            body: {
                transition: 'background-color 0.25s ease, color 0.25s ease',
            },
            '@keyframes iconSpinOnce': {
                from: { transform: 'rotate(0deg)' },
                to: { transform: 'rotate(360deg)' },
            },
            '.spin-on-click': {
                animation: 'iconSpinOnce 0.5s ease-out',
            },
        },
    },
    MuiAppBar: {
        styleOverrides: {
            root: {
                backgroundImage: 'none',
                boxShadow: mode === 'dark' ? '0 1px 3px rgba(0,0,0,0.4)' : '0 1px 3px rgba(0,0,0,0.08)',
            },
        },
    },
    MuiDrawer: {
        styleOverrides: {
            paper: {
                borderRight: 'none',
                backgroundImage: 'none',
            },
        },
    },
    MuiListItemButton: {
        styleOverrides: {
            root: {
                borderRadius: 8,
                margin: '2px 8px',
                transition: 'background-color 0.15s ease, color 0.15s ease',
                '&.Mui-selected': {
                    borderRadius: 8,
                },
            },
        },
    },
    MuiListItemIcon: {
        styleOverrides: {
            root: {
                minWidth: 36,
                transition: 'color 0.15s ease',
            },
        },
    },
    MuiAvatar: {
        styleOverrides: {
            root: {
                transition: 'box-shadow 0.2s ease, transform 0.15s ease',
            },
        },
    },
    MuiBadge: {
        styleOverrides: {
            badge: {
                fontSize: '0.65rem',
                minWidth: 16,
                height: 16,
                padding: '0 4px',
            },
        },
    },
    MuiMenu: {
        styleOverrides: {
            paper: {
                borderRadius: 12,
                ...(mode === 'dark'
                    ? { boxShadow: '0 8px 32px rgba(0,0,0,0.45)' }
                    : { boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }),
            },
        },
    },
    MuiMenuItem: {
        styleOverrides: {
            root: {
                borderRadius: 6,
                margin: '2px 8px',
                transition: 'background-color 0.12s ease',
            },
        },
    },
    MuiButton: {
        styleOverrides: {
            root: {
                borderRadius: 8,
                textTransform: 'none',
                fontWeight: 500,
                transition: 'transform 0.1s ease, background-color 0.15s ease, box-shadow 0.15s ease',
                '&:active': {
                    transform: 'scale(0.97)',
                },
            },
        },
    },
    MuiIconButton: {
        styleOverrides: {
            root: {
                transition: 'transform 0.1s ease, background-color 0.15s ease',
                '&:active': {
                    transform: 'scale(0.90)',
                },
            },
        },
    },
    MuiDialog: {
        styleOverrides: {
            paper: {
                borderRadius: 16,
                ...(mode === 'dark'
                    ? { boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }
                    : { boxShadow: '0 24px 64px rgba(0,0,0,0.14)' }),
            },
        },
    },
    MuiTextField: {
        defaultProps: {
            variant: 'outlined',
            size: 'small',
        },
    },
    // On MuiOutlinedInput directly (not scoped under MuiTextField) so bare
    // <Select> controls get the same 10px radius and focus glow as TextFields.
    MuiOutlinedInput: {
        styleOverrides: {
            root: {
                borderRadius: 10,
                transition: 'box-shadow 0.2s ease',
                '&.Mui-focused': {
                    boxShadow: mode === 'dark' ? '0 0 0 3px rgba(77,166,255,0.18)' : '0 0 0 3px rgba(25,118,210,0.12)',
                },
            },
        },
    },
    MuiTableRow: {
        styleOverrides: {
            root: {
                transition: 'background-color 0.12s ease',
                '&:nth-of-type(odd):not(.MuiTableRow-head)': {
                    backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.015)' : 'rgba(0,0,0,0.015)',
                },
                '&.MuiTableRow-hover:hover': {
                    backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                },
            },
        },
    },
    MuiTableCell: {
        styleOverrides: {
            root: {
                borderBottom: mode === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
                fontSize: '0.8125rem',
                padding: '10px 16px',
            },
            head: {
                fontWeight: 600,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                paddingTop: 12,
                paddingBottom: 12,
            },
        },
    },
    MuiTablePagination: {
        styleOverrides: {
            root: {
                borderTop: mode === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
            },
            selectLabel: { fontSize: '0.8125rem' },
            displayedRows: { fontSize: '0.8125rem' },
        },
    },
    MuiChip: {
        styleOverrides: {
            root: {
                borderRadius: 8,
                fontWeight: 500,
            },
            sizeSmall: {
                height: 24,
                fontSize: '0.75rem',
            },
        },
    },
});

// ────────────────────────────────────────────────────────────
// Shared typography
// ────────────────────────────────────────────────────────────
const sharedTypography = {
    fontFamily: [
        'Inter',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
    ].join(','),
    h6: { fontWeight: 600, letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 500, letterSpacing: '0.01em' },
    body2: { fontSize: '0.8125rem' },
    button: { fontWeight: 500 },
};

// ────────────────────────────────────────────────────────────
// Shape
// ────────────────────────────────────────────────────────────
const sharedShape = { borderRadius: 10 };

// ════════════════════════════════════════════════════════════
// DARK THEME
// ════════════════════════════════════════════════════════════
export const themeDark = responsiveFontSizes(
    createTheme({
        shape: sharedShape,
        typography: sharedTypography,
        components: {
            ...sharedComponents('dark'),
            MuiTooltip: {
                ...sharedComponents('dark').MuiTooltip,
                styleOverrides: {
                    ...sharedComponents('dark').MuiTooltip.styleOverrides,
                    tooltip: {
                        ...sharedComponents('dark').MuiTooltip.styleOverrides.tooltip,
                        backgroundColor: '#243352',
                        color: '#e8e8e8',
                    },
                },
            },
        },
        palette: {
            mode: 'dark',
            primary: {
                main: '#4da6ff',
                light: '#80c1ff',
                dark: '#0a6dd6',
            },
            secondary: {
                main: blue[400],
            },
            background: {
                default: '#0b1120',
                paper: '#16213a',
                main: '#0b1120',
            },
            common: {
                sectionGray: '#1f2c4a',
                darkgray: '#aaa',
                lightgray: '#444',
                simplegray: '#2a2a2a',
                mediumlightgray: '#141414',
                verylightgray: '#16213a',
                paper: '#16213a',
                // The nav chrome sits a step below the content cards (recessed
                // rail); navBar is the translucent app-bar fill behind its blur.
                navSurface: '#0e1730',
                navBar: 'rgba(13,20,38,0.85)',
                navBorder: 'rgba(148,176,255,0.10)',
                white: '#fff',
                black: '#fff',
            },
            text: {
                primary: '#e8e8e8',
                secondary: '#aaa',
                success: '#4da6ff',
                warning: '#ff9f43',
            },
            light: { main: '#fff' },
            white: { main: '#fff' },
            black: { main: '#000' },
            divider: 'rgba(255,255,255,0.08)',
            // Interactive washes are Scope-Blue tinted, not grey — hover/selected
            // mean "you can act here" (the One Blue Rule). Every component that
            // reads action.hover (table rows, list items, menu items, cards)
            // picks this up automatically.
            action: {
                hover: 'rgba(96,165,250,0.10)',
                hoverOpacity: 0.1,
                selected: 'rgba(96,165,250,0.16)',
                selectedOpacity: 0.16,
                focus: 'rgba(96,165,250,0.2)',
                focusOpacity: 0.2,
            },
            missing: { main: '#64FFDA' },
            events: {
                error: '#ff4444',
                pending: '#9e9e9e',
                processing: '#29b6f6',
                succeeded: '#4da6ff',
                autoFixed: '#43a86c',
                warning: '#ff9f43',
            },
            project: {
                colors: ['#4da6ff', '#ffca28'],
            },
        },
        graph: {
            colors: [
                '#29b6f6',
                '#26c6da',
                '#66bb6a',
                '#ffa726',
                '#ec407a',
                '#ab47bc',
                '#5c6bc0',
                '#ef5350',
                '#ffca28',
                '#9ccc65',
            ],
            otherColor: '#9e9e9e',
        },
        material: darcula,
    }),
);

// ════════════════════════════════════════════════════════════
// LIGHT THEME
// ════════════════════════════════════════════════════════════
export const themeLight = responsiveFontSizes(
    createTheme({
        shape: sharedShape,
        typography: sharedTypography,
        components: {
            ...sharedComponents('light'),
            MuiTooltip: {
                ...sharedComponents('light').MuiTooltip,
                styleOverrides: {
                    ...sharedComponents('light').MuiTooltip.styleOverrides,
                    tooltip: {
                        ...sharedComponents('light').MuiTooltip.styleOverrides.tooltip,
                        backgroundColor: '#334155',
                        color: '#fff',
                    },
                },
            },
        },
        palette: {
            mode: 'light',
            primary: {
                main: '#1976d2',
                light: '#4da3ff',
                dark: '#115293',
            },
            secondary: {
                main: blue[500],
            },
            background: {
                default: '#eef2fa',
                paper: '#ffffff',
                main: '#ffffff',
            },
            common: {
                sectionGray: '#e7eef9',
                darkgray: '#555',
                lightgray: '#ddd',
                simplegray: '#c8c8c8',
                mediumlightgray: '#f0f0f0',
                verylightgray: '#eef3fb',
                paper: '#fff',
                // The nav chrome sits a step below the content cards (recessed
                // rail); navBar is the translucent app-bar fill behind its blur.
                navSurface: '#eaf0fb',
                navBar: 'rgba(238,243,252,0.85)',
                navBorder: 'rgba(15,40,90,0.08)',
                white: '#fff',
                black: '#000',
            },
            text: {
                primary: '#1a1a1a',
                secondary: '#555',
                success: '#00589e',
                warning: '#e65100',
            },
            light: { main: '#ccc' },
            white: { main: '#fff' },
            black: { main: '#000' },
            divider: 'rgba(0,0,0,0.08)',
            // Interactive washes are Scope-Blue tinted, not grey — hover/selected
            // mean "you can act here" (the One Blue Rule). Every component that
            // reads action.hover (table rows, list items, menu items, cards)
            // picks this up automatically.
            action: {
                hover: 'rgba(25,118,210,0.06)',
                hoverOpacity: 0.06,
                selected: 'rgba(25,118,210,0.11)',
                selectedOpacity: 0.11,
                focus: 'rgba(25,118,210,0.12)',
                focusOpacity: 0.12,
            },
            missing: { main: '#49c0a3' },
            events: {
                error: '#e53935',
                pending: '#616161',
                processing: '#03a9f4',
                succeeded: '#1976d2',
                autoFixed: '#43a86c',
                warning: '#f57c00',
            },
            project: {
                colors: ['#1976d2', '#f57c00'],
            },
        },
        graph: {
            colors: [
                '#29b6f6',
                '#26c6da',
                '#66bb6a',
                '#ffa726',
                '#ec407a',
                '#ab47bc',
                '#5c6bc0',
                '#ef5350',
                '#ffca28',
                '#9ccc65',
            ],
            otherColor: '#9e9e9e',
        },
        material: oneLight,
    }),
);
